import { create } from 'zustand';
import { stopAnalytics } from '@/lib/analytics/consent';
import { COOKIE_CONSENT_STORAGE_KEY, readStoredCookieConsent, writeStoredCookieConsent } from '@/lib/cookie-consent/storage';

interface CookieConsentStore {
    /** `null` until this browser makes a choice. */
    analytics: boolean | null;
    preferencesOpen: boolean;
    acceptAnalytics: () => void;
    rejectAnalytics: () => void;
    saveAnalytics: (analytics: boolean) => void;
    openPreferences: () => void;
    /** Closes the dialog without changing the saved choice. */
    closePreferences: () => void;
}

const storedChoice = readStoredCookieConsent();

// Visitors who have not chosen yet should not keep analytics identifiers
// that were stored before this banner existed.
if (storedChoice === null) {
    stopAnalytics();
}

function persistChoice(analytics: boolean) {
    writeStoredCookieConsent({ analytics });
    if (!analytics) stopAnalytics();
}

export const useCookieConsentStore = create<CookieConsentStore>((set) => ({
    analytics: storedChoice?.analytics ?? null,
    preferencesOpen: false,
    acceptAnalytics: () => {
        persistChoice(true);
        set({ analytics: true, preferencesOpen: false });
    },
    rejectAnalytics: () => {
        persistChoice(false);
        set({ analytics: false, preferencesOpen: false });
    },
    saveAnalytics: (analytics) => {
        persistChoice(analytics);
        set({ analytics, preferencesOpen: false });
    },
    openPreferences: () => set({ preferencesOpen: true }),
    closePreferences: () => set({ preferencesOpen: false }),
}));

if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
        if (event.key !== COOKIE_CONSENT_STORAGE_KEY) return;
        const choice = readStoredCookieConsent();
        if (choice === null || !choice.analytics) {
            stopAnalytics();
        }
        useCookieConsentStore.setState({
            analytics: choice?.analytics ?? null,
            preferencesOpen: false,
        });
    });
}
